import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import LLMApiService from "@minohealth/common/services/LLMApiService";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { dataURItoBlob } from "@minohealth/common/utils/files";
import ChatImage from "../../../ChatImage";
import { parseAnokyeError, getUserFriendlyMessage } from "@/services/anokye-error-handler";
import "./style.css";
import { IoCloseCircleOutline } from "react-icons/io5";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { CiCircleCheck } from "react-icons/ci";
import { LuCircleDotDashed } from "react-icons/lu";
import { PiCircleHalfFill } from "react-icons/pi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaRegCheckCircle } from "react-icons/fa";
import { BsGrid, BsListUl } from "react-icons/bs";
import { FiFilter, FiSearch, FiUpload, FiX } from "react-icons/fi";
import UploadDicom from "../../../mopacs-content/pages/upload-dicom/UploadDicom";


dayjs.extend(relativeTime);

// Study data for the studies tab
// const studyData = [
//   {
//     id: '5434354',
//     accessionNumber: '326423343',
//     patientName: 'JOHN HENRY DOE',
//     studyDate: 'Tuesday, March 1, 1990',
//     studyDescription: 'THORAX^1WB_PETCT',
//     requestedProcedure: 'PCT PET WHOLE BODY',
//     institution: 'RAAJ DIAGNOSTICS CENTER',
//     referringPhysician: 'KYEL Dankoo',
//     studyDateFormatted: '5464304745804',
//     thumbnailUrl: 'https://medlineplus.gov/images/Xray_share.jpg',
//   },
//   {
//     id: '5434355',
//     accessionNumber: '326423344',
//     patientName: 'JANE MARIE SMITH',
//     studyDate: 'Friday, April 15, 1985',
//     studyDescription: 'THORAX^1WB_PETCT',
//     requestedProcedure: 'PCT PET WHOLE BODY',
//     institution: 'RAAJ DIAGNOSTICS CENTER',
//     referringPhysician: 'KYEL Dankoo',
//     studyDateFormatted: '5464304745805',
//     thumbnailUrl:'https://static.wixstatic.com/media/ebdd4d_df3141d0c1394a068d5e13c7d3d73ff2~mv2.jpg/v1/fill/w_568,h_380,al_c,lg_1,q_80,enc_avif,quality_auto/ebdd4d_df3141d0c1394a068d5e13c7d3d73ff2~mv2.jpg',
//   },
//   {
//     id: '5434356',
//     accessionNumber: '326423345',
//     patientName: 'ROBERT JAMES JOHNSON',
//     studyDate: 'Monday, July 10, 1978',
//     studyDescription: 'THORAX^1WB_PETCT',
//     requestedProcedure: 'PCT PET WHOLE BODY',
//     institution: 'RAAJ DIAGNOSTICS CENTER',
//     referringPhysician: 'KYEL Dankoo',
//     studyDateFormatted: '5464304745806',
//     thumbnailUrl: 'https://www.ucsfbenioffchildrens.org/-/media/project/ucsf/ucsf-bch/images/medical-tests/hero/pelvis-x-ray-708x556-2x.jpg?rev=1c064a964373497895854bd8d7e41aa7',
//   },
//   {
//     id: '5434357',
//     accessionNumber: '326423346',
//     patientName: 'SARAH ELIZABETH WILLIAMS',
//     studyDate: 'Wednesday, September 22, 1992',
//     studyDescription: 'THORAX^1WB_PETCT',
//     requestedProcedure: 'PCT PET WHOLE BODY',
//     institution: 'RAAJ DIAGNOSTICS CENTER',
//     referringPhysician: 'KYEL Dankoo',
//     studyDateFormatted: '5464304745807',
//     thumbnailUrl: 'https://horizonnb.ca/wp-content/uploads/2023/11/X-ray.jpg'
//   }
// ];

// StudyCard component for displaying individual studies
const StudyCard = ({ study }) => {
  const history = useHistory();
  const handleClick = () => {
    // Navigate to the Study component with the study ID (new structure)
    history.push(`../mopacs/study/${study.studyInstanceUid}`);
  };

  function getRelativeTime(timestamp) {
    return `${dayjs(timestamp).fromNow()}`;
  }

  return (
    <div className="study-card" onClick={handleClick}>
      <div className="study-card__image-container">
        <ChatImage
          srcImage={study?.items?.[0]?.image}
          thumbnail
          className="study-card__image"
        />
      </div>
      <div className="study-card__info">
        <h3 className="study-card__name">
          {study?.patient?.patientName?.replace(/\^/g, " ")?.toUpperCase()} - {study?.studyDescription}
        </h3>
        <div className="study-card__details-grid">
          <div className="study-card__detail-column">
            <p className="study-card__detail">Patient ID: {study?.patient?.patientId}</p>
            <p className="study-card__detail">Modality: {study?.modality?.toUpperCase()}</p>
            {/* Institution / Referring Physician not available in new structure */}
          </div>
          <div className="study-card__detail-column">
          <p className="study-card__detail">Study Date: {dayjs(study?.studyDate).format('dddd, MMMM D, YYYY')}</p>
            <p className="study-card__detail">Study ID: {study?.studyId}</p>
            <p className="study-card__detail">Last Updated: {getRelativeTime(study?.latestUpdate)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// AllReports component for the reports tab with infinite scroll
function AllReports({ userReports, filteredReports, viewMode, user, handleOpenReport, handleAcceptingReviews, getStatusClassName, getStatusLabel, getAllUserReports, loadMoreReports, isLoading, hasMore, searchInput }) {
  const observerTarget = useRef(null);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Don't set up observer if searching (client-side filtering only)
    if (!loadMoreReports || !hasMore || isLoading || searchInput) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreReports();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loadMoreReports, hasMore, isLoading, searchInput]);

  return (
    <div className={`reports-container ${viewMode === "list" ? "list-view" : "grid-view"}`}>
      {isLoading && (!userReports || userReports.length === 0) ? (
        <div className="reports-loading-indicator">
          <AiOutlineLoading3Quarters size={24} />
          <p>Loading reports...</p>
        </div>
      ) : userReports && filteredReports.length > 0 ? (
        <>
          {filteredReports.map((report, index) => (
            <ReportCard
              key={index}
              report={report}
              user={user}
              viewMode={viewMode}
              handleOpenReport={handleOpenReport}
              handleAcceptingReviews={handleAcceptingReviews}
              getStatusClassName={getStatusClassName}
              getStatusLabel={getStatusLabel}
              getAllUserReports={getAllUserReports}
            />
          ))}

          {/* Infinite scroll sentinel - only show when not searching */}
          {hasMore && !searchInput && userReports.length > 0 && (
            <div ref={observerTarget} style={{ height: '20px', width: '100%', gridColumn: '1 / -1' }} />
          )}

          {/* Loading more indicator */}
          {isLoading && userReports.length > 0 && (
            <div className="loading-more-indicator" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem',
              color: '#888',
              gridColumn: '1 / -1'
            }}>
              <AiOutlineLoading3Quarters size={20} className="spinning" />
              <p>Loading more reports...</p>
            </div>
          )}

          {/* No more reports message */}
          {!hasMore && userReports.length > 0 && !searchInput && (
            <div className="no-more-reports" style={{
              textAlign: 'center',
              padding: '1rem',
              color: '#888',
              fontSize: '0.9rem',
              gridColumn: '1 / -1'
            }}>
              <p>No more reports to load</p>
            </div>
          )}
        </>
      ) : userReports && filteredReports.length === 0 ? (
        <div className="no-reports-message">
          <p>There are no reports available</p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

// AllStudies component for the studies tab
function AllStudies({ searchInput, viewMode, studyData, getAllStudies, loadMoreStudies, isLoading, hasMore }) {
  const { user } = useSelector((state) => state.app.datastate);
  const observerTarget = useRef(null);

  useEffect(() => {
    getAllStudies(1);
  }, [user?.currentSession, user?.userToken]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Don't set up observer if searching (client-side filtering only)
    if (!loadMoreStudies || !hasMore || isLoading || searchInput) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreStudies();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loadMoreStudies, hasMore, isLoading, searchInput]);

  const filteredStudies = useMemo(() => {
    if (!searchInput || !searchInput.trim()) {
      return studyData;
    }


    const lowercasedSearchTerm = searchInput?.toLowerCase().trim();

    return studyData?.filter(study => {
      const nameMatch = study?.patient?.patientName?.toLowerCase().includes(lowercasedSearchTerm);
      const studyIdMatch = study?.studyId?.toLowerCase().includes(lowercasedSearchTerm);
      const patientIdMatch = study?.patient?.patientId?.toLowerCase().includes(lowercasedSearchTerm);
      const descriptionMatch = study?.studyDescription?.toLowerCase().includes(lowercasedSearchTerm);

      return nameMatch || studyIdMatch || patientIdMatch || descriptionMatch;
    });
  }, [searchInput,studyData]);

  return (
    <div className="all-studies">
      <div className={`all-studies__grid ${viewMode === 'list' ? 'all-studies__grid--list' : ''}`}>
        {filteredStudies?.length > 0 ? (
          filteredStudies?.map((study, index) => (
            <StudyCard key={index} study={study} />
          ))
        ) : (
          <div className="all-studies__no-results">
            <p>No studies found</p>
          </div>
        )}
      </div>

      {/* Infinite scroll sentinel - only show when not searching */}
      {hasMore && !searchInput && studyData?.length > 0 && (
        <div ref={observerTarget} style={{ height: '20px', width: '100%' }} />
      )}

      {/* Loading more indicator */}
      {isLoading && studyData?.length > 0 && (
        <div className="loading-more-indicator" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '1rem',
          color: '#888'
        }}>
          <AiOutlineLoading3Quarters size={20} className="spinning" />
          <p>Loading more studies...</p>
        </div>
      )}

      {/* No more studies message */}
      {!hasMore && studyData?.length > 0 && !searchInput && (
        <div className="no-more-studies" style={{
          textAlign: 'center',
          padding: '1rem',
          color: '#888',
          fontSize: '0.9rem'
        }}>
          <p>No more studies to load</p>
        </div>
      )}
    </div>
  );
}

// ReportCard component for displaying individual reports with image preview
const ReportCard = ({ report: reportData, user, viewMode, handleOpenReport, handleAcceptingReviews, getStatusClassName, getStatusLabel, getAllUserReports }) => {
  const report = reportData?.report
  const image = reportData?.images?.[0]

  // Early return if report is not available
  if (!report) {
    return null;
  }

  return (
    <div
      onClick={() => handleOpenReport(report)}
      className={`report-section__report ${viewMode === "list" ? "list-view-item" : ""}`}
    >
      <div className="report-image-container">
        <div className="report-sub-image-container">
          {/* {isLoading && (
            <div className="report-image-skeleton">
              <div className="skeleton-pulse"></div>
            </div>
          )} */}
          <ChatImage srcImage={image} thumbnail />
          <span className={`report-status ${getStatusClassName(report?.status)}`}>
            {React.createElement(getStatusLabel(report?.status).icon, { size: 18 })}
            {getStatusLabel(report?.status).tagName}
          </span>
          {report?.acceptedByReviewers && !report.acceptedByReviewers.includes(user.id) &&
            report?.reviewers && report.reviewers.includes(user.id) && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="report-accept-review">
                <button
                  title="Accept Review"
                  className="option-accept-review"
                  onClick={() => {
                    handleAcceptingReviews(true, report._id);
                    handleOpenReport(report);
                  }}>
                  <CiCircleCheck size={16} />
                  <p>Accept</p>
                </button>
                <button
                  title="Decline Review"
                  className="option-decline-review"
                  onClick={() => {
                    handleAcceptingReviews(false, report._id);
                    getAllUserReports();
                  }}>
                  <IoCloseCircleOutline size={16} />
                  <p> Decline</p>
                </button>
              </div>
            )}
        </div>
      </div>
      <div className="report-details-container">
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          <p className="report-name-label">Patient name</p>
          <p className="report-name">{`${report?.patientInfo?.name || 'N/A'}`}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          <p className="report-id-label">Patient ID</p>
          <p className="report-id">{`${report?.patientInfo?.id || 'N/A'}`}</p>
        </div>
        <p className="report-last-opened">Last opened {dayjs(report?.updatedAt).fromNow()}</p>
      </div>
    </div>
  );
};

// Main ReportHistory component
function ReportHistory() {
  let reportHistoryActiveTab = window.localStorage.getItem("reportHistoryActiveTab") || "reports";
  const { user } = useSelector((state) => state.app.datastate);
  const [userReports, setUserReports] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // grid or list
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(reportHistoryActiveTab); // reports or studies
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isReportsLoading, setIsReportsLoading] = useState(false);
  const [reportPagination, setReportPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    hasMore: true
  });
  // Studies state & helpers
  const [studyData, setStudyData] = useState([]);
  const [isStudiesLoading, setIsStudiesLoading] = useState(false);
  const [studiesError, setStudiesError] = useState(null);
  const [studyPagination, setStudyPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    hasMore: true
  });
  const filterRef = useRef(null);
  const history = useHistory();
  
  // Set placeholder text based on active tab
  const searchPlaceholder = activeTab === "reports" 
    ? "Search by Patient Name or ID" 
    : "Search studies by Patient Name, ID, or Description";

  function getStatusClassName(status) {
    if (!status) return "status-draft";

    const statusLower = status.toLowerCase();
    if (statusLower.includes("draft")) return "status-draft";
    if (statusLower.includes("pending")) return "status-pending";
    if (statusLower.includes("review")) return "status-in-review";
    if (statusLower.includes("complete")) return "status-completed";

    return "status-draft"; // Default fallback
  }

  async function handleAcceptingReviews(status, reportId) {
    const { data } = await AnokyeApiService(user?.userToken).AcceptReviews({
      acceptType: "acceptReport",
      reviewerId: user.currentSession,
      inviteeId: user.currentSession,
      status: status,
      reportId: reportId,
    });
  }

  function getStatusLabel(status) {
    if (!status) return { tagName: "Draft", icon: LuCircleDotDashed };

    const statusLower = status.toLowerCase();
    if (statusLower.includes("draft")) return { tagName: "Draft", icon: LuCircleDotDashed };
    if (statusLower.includes("pending")) return { tagName: "Pending Review", icon: PiCircleHalfFill };
    if (statusLower.includes("review")) return { tagName: "In Review", icon: AiOutlineLoading3Quarters };
    if (statusLower.includes("complete")) return { tagName: "Completed", icon: FaRegCheckCircle };

    return { tagName: "Draft", icon: LuCircleDotDashed }; // Default fallback
  }

  const getAllStudies = async (page = 1) => {
    if (!user?.userToken || !user?.currentSession) {
      setStudiesError("Missing authentication or session data");
      return;
    }

    if (isStudiesLoading) return;

    setIsStudiesLoading(true);
    setStudiesError(null);

    try {
      const apiService = LLMApiService(user.userToken);
      const { data } = await apiService.getStudies({ page, limit: 5 });

      // On first page, replace studyData; on subsequent pages, append
      if (page === 1) {
        setStudyData(data?.data || []);
      } else {
        setStudyData(prev => [...prev, ...(data?.data || [])]);
      }

      // Update pagination metadata
      setStudyPagination({
        page: data?.pagination?.page || page,
        limit: data?.pagination?.limit || 5,
        total: data?.pagination?.total || 0,
        hasMore: data?.pagination?.hasMore || false
      });
    } catch (error) {
      // LLM backend returns 404 when there are no studies — treat as empty, not an error
      if (error?.response?.status === 404 && page === 1) {
        setStudyData([]);
        setStudyPagination({ page: 1, limit: 5, total: 0, hasMore: false });
        return;
      }

      const parsedError = parseAnokyeError(error);

      Sentry.captureException(error, {
        extra: {
          context: 'ReportHistory - getAllStudies',
          userId: user?._id || 'undefined',
          userToken: user?.userToken ? 'present' : 'missing',
          page,
          isStudiesLoading,
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      const errorMessage = getUserFriendlyMessage(parsedError.code, "Failed to fetch studies");
      console.error("Failed to fetch studies:", errorMessage);
      setStudiesError(errorMessage);
      if (page === 1) {
        setStudyData([]);
      }
    } finally {
      setIsStudiesLoading(false);
    }
  };

  const loadMoreStudies = () => {
    if (isStudiesLoading || !studyPagination.hasMore) return;
    getAllStudies(studyPagination.page + 1);
  };

  async function getAllUserReports(page = 1) {
    if (!user?.userToken || !user?.currentSession) {
      console.warn('Missing authentication or session data');
      return;
    }

    if (isReportsLoading) return;

    setIsReportsLoading(true);
    try {
      const { data } = await AnokyeApiService(user?.userToken).GetResources({
        resourceType: "reports",
        userId: user.currentSession,
        page,
        limit: 5
      });

      // On first page, replace userReports; on subsequent pages, append
      if (page === 1) {
        setUserReports(data?.data || []);
      } else {
        setUserReports(prev => [...prev, ...(data?.data || [])]);
      }

      // Update pagination metadata
      setReportPagination({
        page: data?.pagination?.page || page,
        limit: data?.pagination?.limit || 5,
        total: data?.pagination?.total || 0,
        hasMore: data?.pagination?.hasMore || false
      });
    } catch (error) {
      const parsedError = parseAnokyeError(error);

      Sentry.captureException(error, {
        extra: {
          context: 'ReportHistory - getAllUserReports',
          userId: user?._id || 'undefined',
          currentSession: user?.currentSession || 'undefined',
          userToken: user?.userToken ? 'present' : 'missing',
          page,
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      const errorMessage = getUserFriendlyMessage(parsedError.code, "Failed to fetch user reports");
      console.error('Failed to fetch user reports:', errorMessage);
      if (page === 1) {
        setUserReports([]);
      }
    } finally {
      setIsReportsLoading(false);
    }
  }

  const loadMoreReports = () => {
    if (isReportsLoading || !reportPagination.hasMore) return;
    getAllUserReports(reportPagination.page + 1);
  };

  useEffect(() => {
    getAllUserReports(1);
  }, [user.currentSession]);
  
  // Update document title based on active tab
  useEffect(() => {
    document.title = activeTab === "reports" ? "Reports History" : "Studies";
  }, [activeTab]);

  function getRelativeTime(timestamp) {
    return `Last opened ${dayjs(timestamp).fromNow()}`;
  }

  function handleFilter(filter) {
    setFilterMenuOpen(false);
    setActiveFilter(filter);
  }

  function handleOpenReport(report) {
    if (!report || !report._id) {
      console.warn('Cannot open report: report or report._id is undefined');
      return;
    }
    history.push({ pathname: `report-overview/${report._id}`, state: { report } });
  }

  const filteredReports = useMemo(() => {
    if (!userReports) return [];

    let filtered;

    if (activeFilter === "all") {
      filtered = userReports;
    } else if (activeFilter === "received") {
      filtered = userReports.filter((report) => report?.reviewers && report.reviewers.includes(user.id));
    } else if (activeFilter === "sent") {
      filtered = userReports.filter((report) => report.createdBy === user.id);
    } else if (activeFilter === "draft") {
      filtered = userReports.filter((report) =>
        report.status && report.status.toLowerCase().includes("draft")
      );
    } else if (activeFilter === "completed") {
      filtered = userReports.filter((report) =>
        report.status && report.status.toLowerCase().includes("complete")
      );
    } else if (activeFilter === "pending") {
      filtered = userReports.filter((report) =>
        report.status && report.status.toLowerCase().includes("pending")
      );
    } else {
      filtered = userReports;
    }

    if (searchInput) {
      return filtered.filter(
        (report) =>
          report?.patientInfo?.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
          report?.patientInfo?.id?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return filtered;
  }, [userReports, activeFilter, searchInput, user.id]);


  const closeUploadModal = () => {
    setUploadModalOpen(false);
    // Reset pagination to page 1 and fetch fresh data
    setStudyPagination({
      page: 1,
      limit: 5,
      total: 0,
      hasMore: true
    });
    getAllStudies(1);
  };

  return (
    <div className="report-section-container">
      <div className="report-section-header">
        <div className="search-and-view-controls">
          <div className="input-container">
            <div className="search-icon">
              <FiSearch size={16} color="#888" />
            </div>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="tabs-container">
            <div className="tab-buttons">
              <button 
                className={`tab-button ${activeTab === "reports" ? "active" : ""}`}
                onClick={() => {setActiveTab("reports")
                  setSearchInput("")
                window.localStorage.setItem("reportHistoryActiveTab", "reports")}}
              >
                Reports
              </button>
              <button 
                className={`tab-button ${activeTab === "studies" ? "active" : ""}`}
                onClick={() => {setActiveTab("studies")
                  setSearchInput("")
                window.localStorage.setItem("reportHistoryActiveTab", "studies")}}
              >
                Studies
              </button>
            </div>
          </div>
          <div className="view-and-filter-controls">
            {activeTab === "studies" && (
              <button 
                className="upload-button" 
                onClick={() => setUploadModalOpen(true)}>
                <FiUpload size={16} />
                <span>Upload</span>
              </button>
            )}
            {activeTab === "reports" && (
              <div className="filter-dropdown" ref={filterRef}>
                <button 
                  className="filter-button" 
                  onClick={() => setFilterMenuOpen(!filterMenuOpen)}>
                  <FiFilter size={16} />
                  <span>
                    {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
                  </span>
                </button>
                {filterMenuOpen && (
                  <div className="filter-menu">
                    <div className="filter-option" onClick={() => handleFilter("all")}>
                      All reports
                    </div>
                    <div className="filter-option" onClick={() => handleFilter("received")}>
                      Received
                    </div>
                    <div className="filter-option" onClick={() => handleFilter("sent")}>
                      Sent
                    </div>
                    <div className="filter-option" onClick={() => handleFilter("draft")}>
                      Draft
                    </div>
                    <div className="filter-option" onClick={() => handleFilter("pending")}>
                      Pending
                    </div>
                    <div className="filter-option" onClick={() => handleFilter("completed")}>
                      Completed
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="view-controls">
              <button
                className={`view-mode-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <BsGrid size={18} />
              </button>
              <button
                className={`view-mode-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <BsListUl size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="reports-wrapper">
        <div
          onClick={() => history.push("/")}
          title="back"
          className="report-history-back-icon"
        >
          <MdOutlineKeyboardBackspace size={20} />
          <p className="history-previous-text">Back</p>
        </div>
        {activeTab === "reports" ? (
          <AllReports
            userReports={userReports}
            filteredReports={filteredReports}
            viewMode={viewMode}
            user={user}
            handleOpenReport={handleOpenReport}
            handleAcceptingReviews={handleAcceptingReviews}
            getStatusClassName={getStatusClassName}
            getStatusLabel={getStatusLabel}
            getAllUserReports={getAllUserReports}
            loadMoreReports={loadMoreReports}
            isLoading={isReportsLoading}
            hasMore={reportPagination.hasMore}
            searchInput={searchInput}
          />
        ) : (
          <AllStudies
            searchInput={searchInput}
            viewMode={viewMode}
            studyData={studyData}
            getAllStudies={getAllStudies}
            loadMoreStudies={loadMoreStudies}
            isLoading={isStudiesLoading}
            hasMore={studyPagination.hasMore}
          />
          )}
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="upload-modal-overlay" onClick={closeUploadModal}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h2>Upload DICOM</h2>
              <button className="upload-modal-close" onClick={closeUploadModal}>
                <FiX size={24} />
              </button>
            </div>
            <div className="upload-modal-body">
              <UploadDicom />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportHistory;
